import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #productSelectionRequest = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const request = ++this.#productSelectionRequest;
    const navigationVersion = this.productCarousel?.navigationVersion ?? 0;
    this.productCarousel?.cancelPendingSelection?.();

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html || !this.isConnected || request !== this.#productSelectionRequest) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // On the product page, keep the existing loop alive and move to the
        // selected variant media instead of replacing the whole gallery.
        // The server renders the selected variant media first in sorted_media.
        if (
          this.classList.contains('media-gallery--vela-rebuilt') &&
          this.dataset.productId === newMediaGallery.dataset.productId &&
          this.dataset.hideVariants !== 'true' &&
          newMediaGallery.dataset.hideVariants !== 'true'
        ) {
          const selectedMediaId = newMediaGallery.dataset.selectedMediaId;

          // Variants without their own featured media should leave the current
          // gallery position unchanged.
          if (!selectedMediaId) return;

          const carousel = this.productCarousel;
          if (carousel && typeof carousel.selectMedia !== 'function') {
            await customElements.whenDefined('vela-product-carousel');
            customElements.upgrade(carousel);
          }
          if (!this.isConnected || request !== this.#productSelectionRequest) return;
          if (carousel && carousel.navigationVersion !== navigationVersion) return;

          if (carousel?.selectMedia(selectedMediaId)) {
            this.dataset.selectedMediaId = selectedMediaId;
            return;
          }
        }

        // Combined listings and galleries that hide variant media can contain
        // an entirely different media set, so those still need replacement.
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    if (this.productCarousel) {
      this.productCarousel.selectIndex(event.detail.index, { animate: false });
    } else {
      this.slideshow?.select(event.detail.index, undefined, { animate: false });
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get productCarousel() {
    return this.querySelector('vela-product-carousel');
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
